import axios, { endpoints } from 'src/utils/axios';

// Notification API service for admin panel

export const notificationApi = {
    /**
     * Get all notifications with optional filtering
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default: 1)
     * @param {number} params.limit - Items per page (default: 20)
     * @param {boolean} params.is_read - Filter by read status
     * @param {string} params.search - Search in title and message
     */
    async getNotifications(params = {}) {
        try {
            const queryParams = new URLSearchParams();

            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.is_read !== undefined) queryParams.append('is_read', params.is_read);
            if (params.search) queryParams.append('search', params.search);

            const queryString = queryParams.toString();
            const url = queryString ? `${endpoints.notifications.list}?${queryString}` : endpoints.notifications.list;

            const response = await axios.get(url);

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Failed to fetch notifications');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error fetching notifications:', error);
            throw error;
        }
    },

    /**
     * Get notification statistics (total, unread, archived counts)
     */
    async getStats() {
        try {
            const response = await axios.get(endpoints.notifications.stats);

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Failed to fetch notification stats');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error fetching notification stats:', error);
            throw error;
        }
    },

    /**
     * Get a single notification by ID
     * @param {number} id - Notification ID
     */
    async getNotification(id) {
        try {
            const response = await axios.get(endpoints.notifications.get(id));

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Failed to fetch notification');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error fetching notification:', error);
            throw error;
        }
    },

    /**
     * Mark a notification as read
     * @param {number} id - Notification ID
     */
    async markAsRead(id) {
        try {
            const response = await axios.patch(endpoints.notifications.markAsRead(id));

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Failed to mark notification as read');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error marking notification as read:', error);
            throw error;
        }
    },

    /**
     * Mark all notifications as read
     */
    async markAllAsRead() {
        try {
            const response = await axios.post(endpoints.notifications.markAllAsRead);

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Failed to mark all notifications as read');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            throw error;
        }
    },

    /**
     * Delete a notification
     * @param {number} id - Notification ID
     */
    async deleteNotification(id) {
        try {
            const response = await axios.delete(endpoints.notifications.delete(id));

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Failed to delete notification');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error deleting notification:', error);
            throw error;
        }
    },

    /**
     * Create a new notification (admin only)
     * @param {Object} notificationData - Notification data
     */
    async createNotification(notificationData) {
        try {
            const response = await axios.post(endpoints.notifications.create, notificationData);

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Failed to create notification');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error creating notification:', error);
            throw error;
        }
    },
};

export default notificationApi;
